import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  alpha,
  useTheme,
  Avatar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  Extension as AlgoIcon,
  EventNote as FollowUpIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  VerifiedUser as KycIcon,
  Login as AutoLoginIcon,
  TrendingUp as PnlIcon,
  Warning as FailedIcon,
  ShoppingCart as OrdersIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { buildCrmApiUrl } from '../../api/apiConfig';
import ProfileActions from './ProfileActions';

const InfoRow = ({ label, value, icon: Icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', cursor: 'default' }}>
    {Icon && (
      <Icon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.25 }} />
    )}
    <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={500} component="span">{value || '-'}</Typography>
    </Box>
  </Box>
);

const SectionCard = ({ title, icon: Icon, children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        height: '100%'
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {Icon && <Icon sx={{ color: 'primary.main', fontSize: 22 }} />}
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      </Box>
      {children}
    </Paper>
  );
};

// ClientProfile is a customer's full profile. As a page it reads the number from the URL;
// the support search embeds it (number prop, embedded: no Back button, no page padding).
const ClientProfile = ({ number: numberProp, embedded = false }) => {
  const theme = useTheme();
  const params = useParams();
  const number = numberProp || params.number;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [enablingAutoLogin, setEnablingAutoLogin] = useState(false);

  const fetchProfile = () => {
    if (!number) return;
    setLoading(true);
    fetch(buildCrmApiUrl(`clients/profile/${number}`), {
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setProfile(data.data);
        else setError(data.error || 'Failed to load');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const handleEnableAutoLogin = () => {
    setEnablingAutoLogin(true);
    fetch(buildCrmApiUrl(`clients/profile/${number}/enable-auto-login`), {
      method: 'POST',
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) fetchProfile();
      })
      .finally(() => setEnablingAutoLogin(false));
  };

  // OTP section: fetches temp_otp for the user on demand.
  const [otp, setOtp] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const fetchOtp = () => {
    if (!number) return;
    setOtpLoading(true);
    fetch(buildCrmApiUrl(`clients/otp/${number}`), {
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((res) => res.json())
      .then((data) => { if (data.success) setOtp(data.data?.otp ?? ''); })
      .catch(() => {})
      .finally(() => setOtpLoading(false));
  };

  useEffect(() => {
    if (!number) return;
    setError(null);
    fetchProfile();
  }, [number]);

  if (loading && !profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Box textAlign="center">
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography color="text.secondary">Loading profile...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ p: embedded ? 0 : 3 }}>
        {!embedded && <Button startIcon={<BackIcon />} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/sales'))} sx={{ mb: 2 }}>Back</Button>}
        <Paper variant={embedded ? 'outlined' : undefined} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="error" variant="h6">{error || 'Profile not found'}</Typography>
        </Paper>
      </Box>
    );
  }

  const client = profile.client || {};
  const user = profile.obalgo_user || {};
  const transactions = profile.transactions || [];
  const accesses = profile.accesses || [];
  const asubs = profile.asubs || [];
  const failedPayments = profile.failed_payments || [];
  const obuserUser = profile.obuser_user || {};
  const todayOrders = profile.today_orders || [];
  const followups = profile.followups || [];
  const fmtArr = (v) => (Array.isArray(v) ? v.join(', ') : (v != null ? String(v) : '-'));
  const fmtNum = (v) => (v != null && typeof v === 'number' ? `₹${v.toLocaleString()}` : '-');
  const customFields = client.custom_fields || {};
  const clientData = typeof user.client === 'object' ? user.client : {};

  return (
    <Box sx={embedded ? {} : { p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {!embedded && (
        <Button
          startIcon={<BackIcon />}
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/sales'))}
          sx={{ mb: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Back
        </Button>
      )}

      <ProfileActions number={number} profile={profile} onChanged={fetchProfile} />

      {/* OTP — fetches temp_otp on demand (only for people allowed to see it) */}
      {profile.can_view_otp && <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight={700}>OTP</Typography>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 3 }}>
            {otpLoading ? '…' : (otp != null ? (otp || 'No OTP set') : '••••••')}
          </Typography>
          <Button size="small" variant="outlined" onClick={fetchOtp} disabled={otpLoading}>
            {otp != null ? 'Refresh OTP' : 'Show OTP'}
          </Button>
        </Box>
      </Paper>}

      {/* Profile Header */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
        }}
      >
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={3}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: theme.palette.primary.main,
              fontSize: '1.75rem',
              fontWeight: 700
            }}
          >
            {(user.name || number || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {user.name || 'Client'}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" sx={{ cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
              <Chip
                icon={<PhoneIcon sx={{ fontSize: 16 }} />}
                label={number}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, cursor: 'default' }}
                onClick={(e) => e.preventDefault()}
              />
              {user.email && (
                <Chip
                  icon={<EmailIcon sx={{ fontSize: 16 }} />}
                  label={user.email}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'default' }}
                  onClick={(e) => e.preventDefault()}
                />
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
            {user.plan_name && (
              <Chip label={user.plan_name} color="primary" size="small" sx={{ fontWeight: 600, cursor: 'default' }} onClick={(e) => e.preventDefault()} />
            )}
            {user.deadline && (
              <Chip
                icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                label={`Deadline: ${new Date(user.deadline).toLocaleDateString()}`}
                size="small"
                variant="outlined"
                sx={{ cursor: 'default' }}
                onClick={(e) => e.preventDefault()}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* KYC Stage */}
      {Object.keys(obuserUser).length > 0 && (
        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <KycIcon sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight={700}>KYC & Verification</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Chip
                label={obuserUser.kyc_status ? 'KYC Complete' : 'KYC Pending'}
                color={obuserUser.kyc_status ? 'success' : 'default'}
                size="small"
                sx={{ fontWeight: 600, cursor: 'default' }}
                onClick={(e) => e.preventDefault()}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Chip
                label={obuserUser.ddpi ? 'DDPI Complete' : 'DDPI Pending'}
                color={obuserUser.ddpi ? 'success' : 'default'}
                size="small"
                sx={{ fontWeight: 600, cursor: 'default' }}
                onClick={(e) => e.preventDefault()}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={obuserUser.auto_login ? 'Auto Login On' : 'Auto Login Off'}
                  color={obuserUser.auto_login ? 'success' : 'default'}
                  size="small"
                  sx={{ fontWeight: 600, cursor: 'default' }}
                  onClick={(e) => e.preventDefault()}
                />
                {!obuserUser.auto_login && (
                  <Button size="small" variant="outlined" onClick={handleEnableAutoLogin} disabled={enablingAutoLogin}>
                    {enablingAutoLogin ? 'Enabling...' : 'Enable'}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
          {obuserUser.kyc_details && Object.keys(obuserUser.kyc_details).length > 0 && (
            <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>KYC Details (status)</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} sx={{ cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                {Object.entries(obuserUser.kyc_details).map(([k, v]) => (
                  <Chip key={k} label={`${k}: ${String(v)}`} size="small" variant="outlined" sx={{ cursor: 'default' }} onClick={(e) => e.preventDefault()} />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Client Info */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Client Info" icon={PersonIcon}>
            <Box sx={{ '& > *:last-child': { borderBottom: 'none' } }}>
              <InfoRow label="Number" value={user.number || number} icon={PhoneIcon} />
              <InfoRow label="Email" value={user.email} icon={EmailIcon} />
              <InfoRow label="Profile ID" value={user.profile_id} />
              <InfoRow label="Region" value={client.region || clientData.region} />
              <InfoRow label="Lead Score" value={client.lead_score ?? clientData.lead_score} />
              <InfoRow label="Plan" value={user.plan_name} />
              <InfoRow label="Deadline" value={user.deadline ? new Date(user.deadline).toLocaleString() : null} />
              <InfoRow label="KYC" value={user.kyc} />
              <InfoRow label="Successful Payments" value={user.successful_payments} />
              <InfoRow label="Failed Payments" value={user.failed_payments} />
            </Box>
            {Object.keys(customFields).length > 0 && (
              <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>Custom Fields</Typography>
                {Object.entries(customFields).map(([k, v]) => (
                  <InfoRow key={k} label={k} value={String(v)} />
                ))}
              </Box>
            )}
          </SectionCard>
        </Grid>

        {/* Transactions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Transactions (Last 12 months)" icon={ReceiptIcon}>
            <TableContainer sx={{ maxHeight: 320, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan Assets</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx, i) => (
                    <TableRow key={i} sx={{ cursor: 'default', '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.action.hover, 0.02) } }}>
                      <TableCell>{(tx.paid_at || tx.created_at) ? new Date(tx.paid_at || tx.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>₹{Number(tx.display_amount ?? tx.amount ?? 0).toLocaleString()}</TableCell>
                      <TableCell>{tx.plan_name || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={fmtArr(tx.plan_assets)}>{fmtArr(tx.plan_assets)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {transactions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No transactions</Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Accesses - iterate plan_assets with corresponding deadlines */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Accesses" icon={SecurityIcon}>
            <TableContainer sx={{ maxHeight: 220, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan Asset</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Deadline</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const rows = [];
                    const toArr = (v) => {
                      if (Array.isArray(v)) return v;
                      if (typeof v === 'string') {
                        try {
                          const parsed = JSON.parse(v.replace(/'/g, '"'));
                          return Array.isArray(parsed) ? parsed : [v];
                        } catch {
                          return v ? v.split(',').map(s => s.trim().replace(/^["{}]+|["{}]+$/g, '')) : [];
                        }
                      }
                      return [];
                    };
                    accesses.forEach((a, i) => {
                      const assets = toArr(a.plan_assets);
                      const deadlines = toArr(a.plan_assets_deadline);
                      if (assets.length === 0) {
                        rows.push({ key: `a-${i}`, plan: a.plan_name, asset: '-', deadline: a.deadline ? new Date(a.deadline).toLocaleString() : '-' });
                      } else {
                        assets.forEach((asset, j) => {
                          const dl = deadlines[j] || a.deadline;
                          rows.push({ key: `a-${i}-${j}`, plan: a.plan_name, asset: asset || '-', deadline: dl ? new Date(dl).toLocaleString() : '-' });
                        });
                      }
                    });
                    return rows.map((r) => (
                      <TableRow key={r.key} sx={{ cursor: 'default', '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.action.hover, 0.02) } }}>
                        <TableCell>{r.plan || '-'}</TableCell>
                        <TableCell>{r.asset}</TableCell>
                        <TableCell>{r.deadline}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            {accesses.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No accesses</Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Algo Subscriptions (with net_pnl, gross_pnl from algo_info) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Algo Subscriptions" icon={AlgoIcon}>
            <TableContainer sx={{ maxHeight: 260, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Algo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Gross PnL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Net PnL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {asubs.map((a, i) => {
                    let info = a.algo_info;
                    if (typeof info === 'string') {
                      try { info = JSON.parse(info || '{}'); } catch { info = {}; }
                    }
                    info = (info && typeof info === 'object') ? info : {};
                    const gross = info.gross_pnl ?? info.grossPnL;
                    const net = info.net_pnl ?? info.netPnL;
                    const algoLabel = a.algo_name ? `${a.algo_name} (${a.algo_id || ''})` : (a.algo_id || '-');
                    return (
                      <TableRow key={i} sx={{ cursor: 'default', '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.action.hover, 0.02) } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{algoLabel}</TableCell>
                        <TableCell>
                          <Chip
                            label={a.status || '-'}
                            size="small"
                            color={a.status === 'Live' ? 'success' : a.status === 'Paused' ? 'warning' : 'default'}
                            sx={{ fontWeight: 600, cursor: 'default' }}
                            onClick={(e) => e.preventDefault()}
                          />
                        </TableCell>
                        <TableCell align="right">{a.qty ?? '-'}</TableCell>
                        <TableCell align="right" sx={{ color: (gross ?? 0) >= 0 ? 'success.main' : 'error.main' }}>{fmtNum(gross)}</TableCell>
                        <TableCell align="right" sx={{ color: (net ?? 0) >= 0 ? 'success.main' : 'error.main' }}>{fmtNum(net)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {asubs.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No algo subscriptions</Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Failed Payments */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Failed Payments" icon={FailedIcon}>
            <TableContainer sx={{ maxHeight: 200, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.error.main, 0.08) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedPayments.map((fp, i) => (
                    <TableRow key={i} sx={{ cursor: 'default', '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.action.hover, 0.02) } }}>
                      <TableCell>{fp.created_at ? new Date(fp.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">₹{Number(fp.amount || 0).toLocaleString()}</TableCell>
                      <TableCell>{fp.plan_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {failedPayments.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No failed payments</Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Today's Orders */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Today's Trades" icon={OrdersIcon}>
            <TableContainer sx={{ maxHeight: 220, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Transaction Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Algo</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Avg</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rejection Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayOrders.map((o, i) => (
                    <TableRow key={i} sx={{ cursor: 'default', '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.action.hover, 0.02) } }}>
                      <TableCell>{o.trading_symbol || '-'}</TableCell>
                      <TableCell>{o.type || o.order_type || '-'}</TableCell>
                      <TableCell>{o.transaction_type || '-'}</TableCell>
                      <TableCell>{o.algorithm || o.algo || '-'}</TableCell>
                      <TableCell align="right">{o.quantity ?? o.matched_quantity ?? '-'}</TableCell>
                      <TableCell align="right">{o.avg ?? o.price ?? '-'}</TableCell>
                      <TableCell>{o.created_at || o.order_time ? new Date(o.created_at || o.order_time).toLocaleString() : '-'}</TableCell>
                      <TableCell>{o.status || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.rejection_reason || ''}>{o.rejection_reason || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {todayOrders.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No trades today</Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Follow-ups */}
        <Grid size={{ xs: 12 }}>
          <SectionCard title="Follow-ups" icon={FollowUpIcon}>
            {followups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No follow-ups</Typography>
            ) : (
              <TableContainer sx={{ borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Remind At</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {followups.map((f) => (
                      <TableRow key={f.id} sx={{ cursor: 'default', '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.action.hover, 0.02) } }}>
                        <TableCell>{new Date(f.remind_at).toLocaleString()}</TableCell>
                        <TableCell>{f.note}</TableCell>
                        <TableCell>
                          <Chip
                            label={f.status}
                            size="small"
                            color={f.status === 'done' ? 'success' : 'warning'}
                            icon={f.status === 'done' ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
                            sx={{ fontWeight: 600, cursor: 'default' }}
                            onClick={(e) => e.preventDefault()}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientProfile;
